import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './ForgotPassword.css';
import { forgotPassword, verifyOTP, resetPassword } from '../services/authService';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [verifiedOTP, setVerifiedOTP] = useState('');

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      setError('');
    }
  };

  const validateEmail = () => {
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call API to send OTP to email
      const response = await forgotPassword(email);
      
      // Check if request was successful
      if (response.success) {
        setVerificationEmail(email);
        setShowOTPVerification(true);
        setSuccessMessage(response.message || 'OTP has been sent to your email address.');
      } else {
        setError(response.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      setOtpError('');
      // Focus last input
      document.getElementById('otp-5').focus();
    }
  };

  const handleVerifyOTP = async () => {
    const enteredOTP = otp.join('');
    
    if (enteredOTP.length !== 6) {
      setOtpError('Please enter complete 6-digit OTP');
      return;
    }

    setLoading(true);
    setOtpError('');

    try {
      // Call API to verify OTP
      const response = await verifyOTP(verificationEmail, enteredOTP);
      
      // Check if verification was successful
      if (response.success) {
        // Store reset token if provided (optional)
        if (response.data?.resetToken) {
          setResetToken(response.data.resetToken);
        }
        
        // Store verified OTP for password reset
        setVerifiedOTP(enteredOTP);
        
        // Show reset password form
        setShowOTPVerification(false);
        setShowResetPassword(true);
        setOtp(['', '', '', '', '', '']);
      } else {
        setOtpError(response.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setOtpError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setOtpError('');
    setOtp(['', '', '', '', '', '']);

    try {
      // Call API to resend OTP
      const response = await forgotPassword(verificationEmail);
      
      // Check if request was successful
      if (response.success) {
        setSuccessMessage(response.message || 'OTP has been resent to your email address.');
      } else {
        setOtpError(response.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (err) {
      setOtpError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClosePopup = () => {
    setShowSuccessPopup(false);
    // Navigate to login after closing popup
    setTimeout(() => {
      navigate('/login');
    }, 500);
  };

  const validatePassword = () => {
    if (!newPassword.trim()) {
      setPasswordError('Password is required');
      return false;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    setPasswordError('');

    try {
      // Call API to reset password using verified OTP
      const response = await resetPassword(verificationEmail, verifiedOTP, newPassword);
      
      // Check if reset was successful
      if (response.success) {
        // Show success popup
        setShowResetPassword(false);
        setShowSuccessPopup(true);
        setSuccessMessage(response.message || 'Your password has been reset successfully!');
      } else {
        setPasswordError(response.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setPasswordError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If reset password form is shown, render reset password screen
  if (showResetPassword) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header">
            <button onClick={() => setShowResetPassword(false)} className="back-link">
              ← Back
            </button>
            <h1 className="forgot-password-title">Reset Your Password</h1>
            <p className="forgot-password-subtitle">
              Please enter your new password below.
            </p>
          </div>

          <form className="forgot-password-form" onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                New Password <span className="required">*</span>
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                className={`form-input ${passwordError ? 'error' : ''}`}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                placeholder="Enter your new password"
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="required">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className={`form-input ${passwordError ? 'error' : ''}`}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                placeholder="Confirm your new password"
                minLength={6}
              />
              {passwordError && (
                <span className="field-error">{passwordError}</span>
              )}
            </div>

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // If OTP verification is shown, render OTP screen
  if (showOTPVerification) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header">
            <button onClick={() => setShowOTPVerification(false)} className="back-link">
              ← Back
            </button>
            <h1 className="forgot-password-title">Verify Your Email</h1>
            <p className="forgot-password-subtitle">
              We've sent a 6-digit OTP to <strong>{verificationEmail}</strong>
            </p>
            <p className="otp-hint">Please enter the OTP below to verify your email</p>
          </div>

          <div className="otp-verification">
            <div className="otp-input-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  className={`otp-input ${otpError ? 'error' : ''}`}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  maxLength={1}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            {otpError && (
              <span className="field-error otp-error">{otpError}</span>
            )}
            {successMessage && (
              <p className="success-message">{successMessage}</p>
            )}
            <div className="otp-actions">
              <button 
                type="button" 
                className="resend-button" 
                onClick={handleResendOTP}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend OTP'}
              </button>
              <button 
                type="button" 
                className="verify-button" 
                onClick={handleVerifyOTP}
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <Link to="/login" className="back-link">← Back to Login</Link>
          <h1 className="forgot-password-title">Forgot Password?</h1>
          <p className="forgot-password-subtitle">
            Enter your email address and we'll send you an OTP to reset your password.
          </p>
        </div>

        <form className="forgot-password-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${error ? 'error' : ''}`}
              value={email}
              onChange={handleChange}
              placeholder="Enter your email address"
            />
            {error && (
              <span className="field-error">{error}</span>
            )}
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Sending OTP...' : 'Send OTP'}
          </button>
        </form>

        <div className="forgot-password-footer">
          <p>
            Remember your password? <Link to="/login" className="link">Login here</Link>
          </p>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon success">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="popup-title">Success!</h2>
            <p className="popup-message">
              {successMessage || 'Your password has been reset successfully!'}
            </p>
            <p className="popup-submessage">
              You can now login with your new password.
            </p>
            <button className="popup-button" onClick={handleClosePopup}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;

